import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Gauge, Fuel, Wrench, FileText, Car, Plus, Trash2, AlertTriangle, Download,
} from "lucide-react";
import VehicleBodyMap from "@/components/VehicleBodyMap";
import FileAttachments from "@/components/FileAttachments";

const VehiculoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalogs, setCatalogs] = useState({});

  // Data for each tab
  const [kilometraje, setKilometraje] = useState([]);
  const [combustible, setCombustible] = useState([]);
  const [estetica, setEstetica] = useState({ marks: [] });
  const [mantenimiento, setMantenimiento] = useState([]);
  const [tramites, setTramites] = useState([]);

  // Dialogs
  const [kmDialog, setKmDialog] = useState(false);
  const [fuelDialog, setFuelDialog] = useState(false);
  const [maintDialog, setMaintDialog] = useState(false);
  const [tramiteDialog, setTramiteDialog] = useState(false);

  // Form states
  const [kmForm, setKmForm] = useState({ fecha: today(), km_anterior: "", km_actual: "", conductor: "", notas: "" });
  const [fuelForm, setFuelForm] = useState({ fecha: today(), litros: "", costo_total: "", km_actual: "", tipo_combustible: "Magna", estacion: "", notas: "" });
  const [maintForm, setMaintForm] = useState({ fecha: today(), tipo: "Preventivo", descripcion: "", taller: "", costo: "", km_actual: "", proximo_km: "", proxima_fecha: "", factura: "", notas: "" });
  const [tramiteForm, setTramiteForm] = useState({ fecha: today(), tipo_tramite: "", entidad: "", costo: "", vigencia_inicio: "", vigencia_fin: "", numero_referencia: "", resultado: "Aprobado", notas: "" });

  function today() { return new Date().toISOString().split("T")[0]; }
  const fmt = (n) => typeof n === "number" ? n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n;
  const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("es-MX"); } catch { return d; } };

  useEffect(() => {
    fetchAll();
  }, [id]);

  const fetchAll = async () => {
    try {
      const [vRes, catRes, kmRes, fuelRes, estRes, maintRes, tramRes] = await Promise.all([
        axios.get(`${API}/assets`, { withCredentials: true }),
        axios.get(`${API}/vehicles/catalogs`, { withCredentials: true }),
        axios.get(`${API}/vehicles/${id}/kilometraje`, { withCredentials: true }),
        axios.get(`${API}/vehicles/${id}/combustible`, { withCredentials: true }),
        axios.get(`${API}/vehicles/${id}/estetica`, { withCredentials: true }),
        axios.get(`${API}/vehicles/${id}/mantenimiento`, { withCredentials: true }),
        axios.get(`${API}/vehicles/${id}/tramites`, { withCredentials: true }),
      ]);
      const found = vRes.data.find((a) => a.id === id);
      if (!found) { toast.error("Vehículo no encontrado"); navigate("/logistica/vehiculos"); return; }
      setVehicle(found);
      setCatalogs(catRes.data);
      setKilometraje(kmRes.data);
      setCombustible(fuelRes.data);
      setEstetica(estRes.data);
      setMantenimiento(maintRes.data);
      setTramites(tramRes.data);
    } catch (err) {
      console.error("Error loading vehicle data:", err);
      toast.error("Error al cargar datos del vehículo");
    } finally {
      setLoading(false);
    }
  };

  // --- CRUD helpers ---
  const addRecord = async (collection, form, setRecords, closeDialog) => {
    try {
      const res = await axios.post(`${API}/vehicles/${id}/${collection}`, form, { withCredentials: true });
      setRecords((prev) => [res.data, ...prev]);
      toast.success("Registro agregado");
      closeDialog(false);
    } catch (err) { console.error(`Error adding ${collection}:`, err); toast.error("Error al guardar"); }
  };

  const deleteRecord = async (collection, recordId, setRecords) => {
    if (!confirm("¿Eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/vehicles/${id}/${collection}/${recordId}`, { withCredentials: true });
      setRecords((prev) => prev.filter((r) => r.id !== recordId));
      toast.success("Eliminado");
    } catch (err) { console.error(`Error deleting ${collection}:`, err); toast.error("Error al eliminar"); }
  };

  const saveEstetica = async (marks) => {
    try {
      await axios.post(`${API}/vehicles/${id}/estetica`, { marks }, { withCredentials: true });
      setEstetica({ ...estetica, marks });
    } catch (err) { console.error("Error saving estetica:", err); toast.error("Error al guardar"); }
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadBitacoraPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/vehicles/${id}/bitacora-pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bitacora_${vehicle?.clave || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Error downloading PDF:", err); toast.error("Error al generar el PDF"); }
    finally { setPdfLoading(false); }
  };

  if (loading) {
    return <div className="p-6 flex justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  if (!vehicle) return null;

  const lastKm = kilometraje.length > 0 ? kilometraje[0].km_actual : 0;
  const totalFuel = combustible.reduce((s, r) => s + (r.costo_total || 0), 0);
  const totalMaint = mantenimiento.reduce((s, r) => s + (r.costo || 0), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/logistica/vehiculos")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">{vehicle.descripcion}</h1>
          <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
            <span className="font-mono">{vehicle.clave}</span>
            <span>{vehicle.marca} {vehicle.modelo}</span>
            {vehicle.placas && <Badge variant="outline" className="font-mono">{vehicle.placas}</Badge>}
            <Badge className={vehicle.estatus === "propio" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
              {vehicle.estatus === "propio" ? "Propio" : "Leasing"}
            </Badge>
          </div>
        </div>
        <Button variant="outline" onClick={downloadBitacoraPDF} disabled={pdfLoading} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="bitacora-pdf-btn">
          <Download className="w-4 h-4 mr-2" /> {pdfLoading ? "Generando…" : "Bitácora PDF"}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3 text-center">
          <Gauge className="w-5 h-5 mx-auto text-blue-600 mb-1" />
          <p className="text-lg font-bold">{lastKm.toLocaleString()} km</p>
          <p className="text-xs text-slate-500">Último kilometraje</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center">
          <Fuel className="w-5 h-5 mx-auto text-green-600 mb-1" />
          <p className="text-lg font-bold">${fmt(totalFuel)}</p>
          <p className="text-xs text-slate-500">Gasto combustible</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center">
          <Wrench className="w-5 h-5 mx-auto text-amber-600 mb-1" />
          <p className="text-lg font-bold">${fmt(totalMaint)}</p>
          <p className="text-xs text-slate-500">Gasto mantenimiento</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center">
          <AlertTriangle className="w-5 h-5 mx-auto text-red-500 mb-1" />
          <p className="text-lg font-bold">{estetica.marks?.length || 0}</p>
          <p className="text-xs text-slate-500">Incidencias estética</p>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="kilometraje" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 h-auto">
          <TabsTrigger value="kilometraje" className="text-xs sm:text-sm py-2" data-testid="tab-km">Kilometraje</TabsTrigger>
          <TabsTrigger value="combustible" className="text-xs sm:text-sm py-2" data-testid="tab-fuel">Combustible</TabsTrigger>
          <TabsTrigger value="estetica" className="text-xs sm:text-sm py-2" data-testid="tab-body">Estética</TabsTrigger>
          <TabsTrigger value="mantenimiento" className="text-xs sm:text-sm py-2" data-testid="tab-maint">Mantenimiento</TabsTrigger>
          <TabsTrigger value="tramites" className="text-xs sm:text-sm py-2" data-testid="tab-tramites">Trámites</TabsTrigger>
        </TabsList>

        {/* === KILOMETRAJE === */}
        <TabsContent value="kilometraje">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-blue-600" /> Bitácora de Kilometraje</CardTitle>
              <Button size="sm" onClick={() => { setKmForm({ fecha: today(), km_anterior: lastKm || "", km_actual: "", conductor: vehicle.responsable_nombre || "", notas: "" }); setKmDialog(true); }} data-testid="add-km-btn">
                <Plus className="w-4 h-4 mr-1" /> Registrar
              </Button>
            </CardHeader>
            <CardContent>
              {kilometraje.length === 0 ? <p className="text-center text-slate-400 py-8">Sin registros de kilometraje</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fecha</TableHead><TableHead>Km Anterior</TableHead><TableHead>Km Actual</TableHead><TableHead>Recorrido</TableHead><TableHead>Conductor</TableHead><TableHead>Notas</TableHead><TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{kilometraje.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{fmtDate(r.fecha)}</TableCell>
                      <TableCell className="font-mono">{r.km_anterior?.toLocaleString()}</TableCell>
                      <TableCell className="font-mono font-medium">{r.km_actual?.toLocaleString()}</TableCell>
                      <TableCell><Badge variant="secondary">{(r.km_actual - r.km_anterior).toLocaleString()} km</Badge></TableCell>
                      <TableCell>{r.conductor}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{r.notas || "—"}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => deleteRecord("kilometraje", r.id, setKilometraje)}><Trash2 className="w-4 h-4 text-red-400" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === COMBUSTIBLE === */}
        <TabsContent value="combustible">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Fuel className="w-5 h-5 text-green-600" /> Bitácora de Combustible</CardTitle>
              <Button size="sm" onClick={() => { setFuelForm({ fecha: today(), litros: "", costo_total: "", km_actual: lastKm || "", tipo_combustible: "Magna", estacion: "", notas: "" }); setFuelDialog(true); }} data-testid="add-fuel-btn">
                <Plus className="w-4 h-4 mr-1" /> Registrar Carga
              </Button>
            </CardHeader>
            <CardContent>
              {combustible.length === 0 ? <p className="text-center text-slate-400 py-8">Sin registros de combustible</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Litros</TableHead><TableHead>Costo Total</TableHead><TableHead>$/Litro</TableHead><TableHead>Km</TableHead><TableHead>Estación</TableHead><TableHead>Archivos</TableHead><TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{combustible.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{fmtDate(r.fecha)}</TableCell>
                      <TableCell><Badge variant="secondary">{r.tipo_combustible}</Badge></TableCell>
                      <TableCell className="font-mono">{r.litros}</TableCell>
                      <TableCell className="font-mono font-medium">${fmt(r.costo_total)}</TableCell>
                      <TableCell className="font-mono text-sm">${fmt(r.precio_litro)}</TableCell>
                      <TableCell className="font-mono">{r.km_actual?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{r.estacion || "—"}</TableCell>
                      <TableCell>
                        <FileAttachments assetId={id} collection="combustible" recordId={r.id} archivos={r.archivos || []}
                          onUpdate={(updated) => setCombustible(prev => prev.map(x => x.id === r.id ? {...x, archivos: updated} : x))} />
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => deleteRecord("combustible", r.id, setCombustible)}><Trash2 className="w-4 h-4 text-red-400" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ESTÉTICA === */}
        <TabsContent value="estetica">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Mapa de Estética Vehicular</CardTitle>
            </CardHeader>
            <CardContent>
              <VehicleBodyMap
                marks={estetica.marks || []}
                onSave={saveEstetica}
                vehicleType={vehicle.tipo_vehiculo || "Sedan"}
                assetId={id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* === MANTENIMIENTO === */}
        <TabsContent value="mantenimiento">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-amber-600" /> Bitácora de Mantenimiento</CardTitle>
              <Button size="sm" onClick={() => { setMaintForm({ fecha: today(), tipo: "Preventivo", descripcion: "", taller: "", costo: "", km_actual: lastKm || "", proximo_km: "", proxima_fecha: "", factura: "", notas: "" }); setMaintDialog(true); }} data-testid="add-maint-btn">
                <Plus className="w-4 h-4 mr-1" /> Registrar
              </Button>
            </CardHeader>
            <CardContent>
              {mantenimiento.length === 0 ? <p className="text-center text-slate-400 py-8">Sin registros de mantenimiento</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Descripción</TableHead><TableHead>Taller</TableHead><TableHead>Costo</TableHead><TableHead>Km</TableHead><TableHead>Próximo</TableHead><TableHead>Archivos</TableHead><TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{mantenimiento.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{fmtDate(r.fecha)}</TableCell>
                      <TableCell><Badge className={r.tipo === "Preventivo" ? "bg-blue-100 text-blue-700" : r.tipo === "Correctivo" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}>{r.tipo}</Badge></TableCell>
                      <TableCell className="max-w-[200px] truncate">{r.descripcion}</TableCell>
                      <TableCell className="text-sm">{r.taller || "—"}</TableCell>
                      <TableCell className="font-mono">${fmt(r.costo)}</TableCell>
                      <TableCell className="font-mono">{r.km_actual?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{r.proxima_fecha ? fmtDate(r.proxima_fecha) : r.proximo_km ? `${r.proximo_km.toLocaleString()} km` : "—"}</TableCell>
                      <TableCell>
                        <FileAttachments assetId={id} collection="mantenimiento" recordId={r.id} archivos={r.archivos || []}
                          onUpdate={(updated) => setMantenimiento(prev => prev.map(x => x.id === r.id ? {...x, archivos: updated} : x))} />
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => deleteRecord("mantenimiento", r.id, setMantenimiento)}><Trash2 className="w-4 h-4 text-red-400" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TRÁMITES === */}
        <TabsContent value="tramites">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-purple-600" /> Bitácora de Trámites Vehiculares</CardTitle>
              <Button size="sm" onClick={() => { setTramiteForm({ fecha: today(), tipo_tramite: "", entidad: "", costo: "", vigencia_inicio: "", vigencia_fin: "", numero_referencia: "", resultado: "Aprobado", notas: "" }); setTramiteDialog(true); }} data-testid="add-tramite-btn">
                <Plus className="w-4 h-4 mr-1" /> Registrar
              </Button>
            </CardHeader>
            <CardContent>
              {tramites.length === 0 ? <p className="text-center text-slate-400 py-8">Sin registros de trámites</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fecha</TableHead><TableHead>Trámite</TableHead><TableHead>Entidad</TableHead><TableHead>Costo</TableHead><TableHead>Vigencia</TableHead><TableHead>Referencia</TableHead><TableHead>Resultado</TableHead><TableHead>Archivos</TableHead><TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{tramites.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{fmtDate(r.fecha)}</TableCell>
                      <TableCell className="font-medium">{r.tipo_tramite}</TableCell>
                      <TableCell className="text-sm">{r.entidad || "—"}</TableCell>
                      <TableCell className="font-mono">${fmt(r.costo)}</TableCell>
                      <TableCell className="text-xs">{r.vigencia_inicio && r.vigencia_fin ? `${fmtDate(r.vigencia_inicio)} - ${fmtDate(r.vigencia_fin)}` : "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{r.numero_referencia || "—"}</TableCell>
                      <TableCell>
                        <Badge className={r.resultado === "Aprobado" ? "bg-green-100 text-green-700" : r.resultado === "Rechazado" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{r.resultado}</Badge>
                      </TableCell>
                      <TableCell>
                        <FileAttachments assetId={id} collection="tramites" recordId={r.id} archivos={r.archivos || []}
                          onUpdate={(updated) => setTramites(prev => prev.map(x => x.id === r.id ? {...x, archivos: updated} : x))} />
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => deleteRecord("tramites", r.id, setTramites)}><Trash2 className="w-4 h-4 text-red-400" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ DIALOGS ============ */}

      {/* KM Dialog */}
      <Dialog open={kmDialog} onOpenChange={setKmDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar Kilometraje</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={kmForm.fecha} onChange={(e) => setKmForm({...kmForm, fecha: e.target.value})} data-testid="km-fecha" /></div>
              <div className="space-y-2"><Label>Conductor</Label><Input value={kmForm.conductor} onChange={(e) => setKmForm({...kmForm, conductor: e.target.value})} data-testid="km-conductor" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Km Anterior</Label><Input type="number" value={kmForm.km_anterior} onChange={(e) => setKmForm({...kmForm, km_anterior: e.target.value})} data-testid="km-anterior" /></div>
              <div className="space-y-2"><Label>Km Actual</Label><Input type="number" value={kmForm.km_actual} onChange={(e) => setKmForm({...kmForm, km_actual: e.target.value})} data-testid="km-actual" /></div>
            </div>
            <div className="space-y-2"><Label>Notas</Label><Input value={kmForm.notas} onChange={(e) => setKmForm({...kmForm, notas: e.target.value})} placeholder="Observaciones..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setKmDialog(false)}>Cancelar</Button><Button onClick={() => addRecord("kilometraje", kmForm, setKilometraje, setKmDialog)} data-testid="save-km-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fuel Dialog */}
      <Dialog open={fuelDialog} onOpenChange={setFuelDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar Carga de Combustible</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={fuelForm.fecha} onChange={(e) => setFuelForm({...fuelForm, fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Tipo</Label>
                <Select value={fuelForm.tipo_combustible} onValueChange={(v) => setFuelForm({...fuelForm, tipo_combustible: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(catalogs.tipos_combustible || ["Magna","Premium","Diesel"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Litros</Label><Input type="number" step="0.01" value={fuelForm.litros} onChange={(e) => setFuelForm({...fuelForm, litros: e.target.value})} /></div>
              <div className="space-y-2"><Label>Costo Total ($)</Label><Input type="number" step="0.01" value={fuelForm.costo_total} onChange={(e) => setFuelForm({...fuelForm, costo_total: e.target.value})} /></div>
              <div className="space-y-2"><Label>Km Actual</Label><Input type="number" value={fuelForm.km_actual} onChange={(e) => setFuelForm({...fuelForm, km_actual: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Estación</Label><Input value={fuelForm.estacion} onChange={(e) => setFuelForm({...fuelForm, estacion: e.target.value})} placeholder="Nombre de la gasolinera" /></div>
              <div className="space-y-2"><Label>Notas</Label><Input value={fuelForm.notas} onChange={(e) => setFuelForm({...fuelForm, notas: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFuelDialog(false)}>Cancelar</Button><Button onClick={() => addRecord("combustible", fuelForm, setCombustible, setFuelDialog)} data-testid="save-fuel-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintDialog} onOpenChange={setMaintDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registrar Mantenimiento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={maintForm.fecha} onChange={(e) => setMaintForm({...maintForm, fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Tipo</Label>
                <Select value={maintForm.tipo} onValueChange={(v) => setMaintForm({...maintForm, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(catalogs.tipos_mantenimiento || ["Preventivo","Correctivo"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Descripción del Servicio</Label><Input value={maintForm.descripcion} onChange={(e) => setMaintForm({...maintForm, descripcion: e.target.value})} placeholder="Ej: Cambio de aceite y filtros" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Taller / Agencia</Label><Input value={maintForm.taller} onChange={(e) => setMaintForm({...maintForm, taller: e.target.value})} /></div>
              <div className="space-y-2"><Label>No. Factura</Label><Input value={maintForm.factura} onChange={(e) => setMaintForm({...maintForm, factura: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Costo ($)</Label><Input type="number" step="0.01" value={maintForm.costo} onChange={(e) => setMaintForm({...maintForm, costo: e.target.value})} /></div>
              <div className="space-y-2"><Label>Km Actual</Label><Input type="number" value={maintForm.km_actual} onChange={(e) => setMaintForm({...maintForm, km_actual: e.target.value})} /></div>
              <div className="space-y-2"><Label>Próximo Km</Label><Input type="number" value={maintForm.proximo_km} onChange={(e) => setMaintForm({...maintForm, proximo_km: e.target.value})} placeholder="Siguiente servicio" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Próxima Fecha Servicio</Label><Input type="date" value={maintForm.proxima_fecha} onChange={(e) => setMaintForm({...maintForm, proxima_fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Notas</Label><Input value={maintForm.notas} onChange={(e) => setMaintForm({...maintForm, notas: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMaintDialog(false)}>Cancelar</Button><Button onClick={() => addRecord("mantenimiento", maintForm, setMantenimiento, setMaintDialog)} data-testid="save-maint-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trámites Dialog */}
      <Dialog open={tramiteDialog} onOpenChange={setTramiteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registrar Trámite Vehicular</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={tramiteForm.fecha} onChange={(e) => setTramiteForm({...tramiteForm, fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Tipo de Trámite</Label>
                <Select value={tramiteForm.tipo_tramite} onValueChange={(v) => setTramiteForm({...tramiteForm, tipo_tramite: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{(catalogs.tipos_tramite || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Entidad Federativa</Label>
                <Select value={tramiteForm.entidad} onValueChange={(v) => setTramiteForm({...tramiteForm, entidad: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{(catalogs.entidades || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Costo ($)</Label><Input type="number" step="0.01" value={tramiteForm.costo} onChange={(e) => setTramiteForm({...tramiteForm, costo: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Vigencia Inicio</Label><Input type="date" value={tramiteForm.vigencia_inicio} onChange={(e) => setTramiteForm({...tramiteForm, vigencia_inicio: e.target.value})} /></div>
              <div className="space-y-2"><Label>Vigencia Fin</Label><Input type="date" value={tramiteForm.vigencia_fin} onChange={(e) => setTramiteForm({...tramiteForm, vigencia_fin: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>No. Referencia / Folio</Label><Input value={tramiteForm.numero_referencia} onChange={(e) => setTramiteForm({...tramiteForm, numero_referencia: e.target.value})} /></div>
              <div className="space-y-2"><Label>Resultado</Label>
                <Select value={tramiteForm.resultado} onValueChange={(v) => setTramiteForm({...tramiteForm, resultado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aprobado">Aprobado</SelectItem>
                    <SelectItem value="Rechazado">Rechazado</SelectItem>
                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                    <SelectItem value="En Proceso">En Proceso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Notas</Label><Input value={tramiteForm.notas} onChange={(e) => setTramiteForm({...tramiteForm, notas: e.target.value})} placeholder="Observaciones adicionales..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTramiteDialog(false)}>Cancelar</Button><Button onClick={() => addRecord("tramites", tramiteForm, setTramites, setTramiteDialog)} data-testid="save-tramite-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiculoDetalle;
